// Helpers partagés par les pages de classement (/classement...) : référentiel ligues/
// comités (public, cf. clubs/routes.py::list_ligues/list_comites), saisons avec leur id
// (les routes génériques /leaderboard/{ligue|comite}/{id}/{season_id} prennent l'id, pas
// le libellé) et libellés des colonnes de détail par tournoi (cf. service._breakdown_key).
import backendApi from '@/axios/backendApi';
import { backend_url } from '@/config/config';

export interface Jurisdiction {
  id: number;
  code: string;
  name: string;
  logo: string | null;
  ligue_code?: string | null;
}

export interface SeasonOption {
  id: number;
  label: string;
  start_date: string;
  is_current: boolean;
}

export interface RankingEntry {
  joueur: string;
  joueur_brut?: string;
  pts: number | string;
  clt: number | string;
  [key: string]: string | number | undefined;
}

export interface RankingCategory {
  category: string;
  entries: RankingEntry[];
}

export const logoUrl = (logo: string | null | undefined): string | null => {
  if (!logo) return null;
  return logo.startsWith('/') ? `${backend_url}${logo}` : logo;
};

export const fetchLigues = async (): Promise<Jurisdiction[]> => {
  const { data } = await backendApi.get('/clubs/ligues');
  return data;
};

export const fetchComites = async (ligueCode?: string): Promise<Jurisdiction[]> => {
  const { data } = await backendApi.get('/clubs/comites', { params: ligueCode ? { ligue_code: ligueCode } : {} });
  return data;
};

// Saisons déjà commencées uniquement (en cours + passées) : la table peut contenir des
// saisons futures créées à l'avance, sans classement possible.
export const fetchSeasons = async (): Promise<SeasonOption[]> => {
  const { data } = await backendApi.get<SeasonOption[]>('/seasons');
  const current = data.find((s) => s.is_current);
  const limit = current?.start_date ?? new Date().toISOString().slice(0, 10);
  return data.filter((s) => s.start_date <= limit);
};

// Titre/description/chemin canonique d'une page de classement, miroir de Dartshub
// leaderboards/seo_head.py::leaderboard_head (rendu côté serveur pour les aperçus de
// partage) : saison en cours, celle affichée à l'arrivée sur la page.
export const leaderboardSeo = (
  level: 'federal' | 'ligue_home' | 'ligue' | 'comite',
  season: string,
  ligue: Jurisdiction | null,
  comite?: Jurisdiction | null,
): { title: string; description: string; path: string } => {
  if (level === 'federal') {
    return {
      title: 'Classement - National FFD',
      description: `Classement officiel National FFD - Saison ${season}`,
      path: '/classement/national',
    };
  }
  if (level === 'ligue_home' || level === 'ligue') {
    return {
      title: `Classement - Ligue ${ligue!.name}`,
      description: `Classement officiel de la Ligue ${ligue!.name} - Saison ${season}`,
      path: `/classement/${ligue!.code}${level === 'ligue' ? '/ligue' : ''}`,
    };
  }
  return {
    title: `Classement - Comité ${comite!.name}`,
    description: `Classement officiel du Comité ${comite!.name} - Saison ${season}`,
    path: `/classement/${ligue!.code}/${comite!.code}`,
  };
};

export const sameCode = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

// Libellé d'une colonne de détail : "oc2" -> "Open de Comité #2", "cl" -> "Coupe de la
// Ligue", etc. Code inconnu : affiché en majuscules plutôt que masqué.
const FEDERAL_LABELS: Record<string, string> = {
  of: 'Open Fédéral',
  ic: 'Inter-Comités',
  cl_fed: 'Coupe des Ligues',
  cn: 'Coupe Fédérale',
  cf: 'Coupe de France',
  ct_fed: 'Classement\nTerritorial',
  ms: 'Masters Nationaux',
};

export const columnLabel = (key: string, level: 'comite' | 'ligue' | 'federal'): string => {
  if (level === 'federal') {
    const m = key.match(/^([a-z_]+?)(\d+)?$/);
    const base = m ? FEDERAL_LABELS[m[1]] : undefined;
    if (!base) return key.toUpperCase();
    return m![2] ? `${base} #${m![2]}` : base;
  }
  if (key === 'pts_com') return 'Points\nComité';
  if (key === 'master') return 'Masters Régionaux';
  if (key === 'cc') return 'Coupe du Comité';
  if (key === 'cl') return 'Coupe de la Ligue';
  const m = key.match(/^([a-z_]+?)(\d+)$/);
  if (m) {
    const [, prefix, n] = m;
    if (prefix === 'oc') return `Open de Comité #${n}`;
    if (prefix === 'ol') return `Open de Ligue #${n}`;
    if (prefix === 'e' || prefix === 'hc' || prefix === 'hl') {
      return `${level === 'comite' ? 'Hors Comité' : 'Hors Ligue'} #${n}`;
    }
  }
  return key.toUpperCase();
};

// Code entre parenthèses en fin de nom : "NOM Prénom (CODE-CAT)" -> "CODE" (club au
// niveau comité, comité au niveau ligue).
export const extractEntityCode = (joueur: string): string => {
  const match = joueur.match(/\(([^-]+)-[^)]+\)$/);
  return match ? match[1].trim() : '';
};

// Nom sans aucun code entre parenthèses ("NOM Prénom (CODE-CAT)" -> "NOM Prénom", idem
// pour chaque joueur d'une doublette) : version anonyme des classements, sans club,
// comité ni ligue.
export const stripEntityCodes = (joueur: string): string =>
  joueur.replace(/\s*\([^)]*\)/g, '').trim();

// Export PDF des classements réservé aux responsables de comité / ligue (et super admin).
export const canExportLeaderboard = (scopes: string[]): boolean =>
  scopes.some((s) => s === 'comitee_representative' || s === 'ligue_representative' || s === 'super_admin');

export const hasRankingEntries = (leaderboard: RankingCategory[] | undefined | null): boolean =>
  Array.isArray(leaderboard) && leaderboard.some((cat) => cat.entries && cat.entries.length > 0);

export type LeaderboardLevel = 'comite' | 'ligue' | 'federal';

export interface RankingColumn {
  key: string;
  label: string;
  info: { place: string; date: string };
}

export const formatShortDate = (iso: string | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

// Colonnes = tournois du calendrier (`tournaments`) + clés de détail présentes dans les
// lignes mais sans tournoi propre (types "derived" : pts_com, cl_fed, ct_fed). Ligue et
// comité du joueur (fédéral) ne sont pas des points : colonnes à part (LeaderboardScope).
const ENTRY_BASE_KEYS = new Set(['joueur', 'joueur_brut', 'pts', 'clt', 'code_clt', 'ligue', 'comite']);
export const rankingColumns = (
  leaderboard: RankingCategory[],
  tournaments: Record<string, { place: string; date: string }>,
  level: LeaderboardLevel,
): RankingColumn[] => {
  const keys = Object.keys(tournaments);
  for (const cat of leaderboard) for (const e of cat.entries) for (const k of Object.keys(e)) {
    if (!ENTRY_BASE_KEYS.has(k) && !keys.includes(k)) keys.push(k);
  }
  return keys.map((key) => ({
    key, label: columnLabel(key, level), info: tournaments[key] || { place: '', date: '' },
  }));
};

// Message postMessage envoyé par la page /embed/... à la page qui l'intègre, pour que
// l'iframe prenne la hauteur du classement (cf. LeaderboardEmbed.vue).
export const EMBED_HEIGHT_MESSAGE = 'dartshub-embed-height';

// Thème de la page intégrée (?theme=clair|sombre, sombre par défaut) : au choix du
// comité / de la ligue selon le fond de son site.
export type EmbedTheme = 'sombre' | 'clair';

// Code HTML à coller sur un site tiers (WordPress : bloc "HTML personnalisé"). Sans le
// <script>, l'iframe reste à 800px avec une barre de défilement interne.
export const buildEmbedCode = (embedUrl: string, title: string): string => {
  const origin = new URL(embedUrl).origin;
  const safeTitle = title.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  return `<iframe src="${embedUrl}" title="${safeTitle}" style="width:100%;height:800px;border:0;" loading="lazy"></iframe>
<script>
window.addEventListener('message', function (e) {
  if (e.origin !== '${origin}' || !e.data || e.data.type !== '${EMBED_HEIGHT_MESSAGE}') return;
  document.querySelectorAll('iframe').forEach(function (f) {
    if (f.contentWindow === e.source) f.style.height = e.data.height + 'px';
  });
});
</script>`;
};
